import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Heart, Eye, Shield, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';
import { useMockData } from '../data/useMockData';

const valueIcons = {
  'Quality': Shield,
  'Qualité': Shield,
  'الجودة': Shield,
  'Transparency': Eye,
  'Transparence': Eye,
  'الشفافية': Eye,
  'Ownership': Heart,
  'ملكية': Heart,
  'Continuous Learning': BookOpen,
  'Apprentissage Continu': BookOpen,
  'التعلم المستمر': BookOpen,
};

const AboutPage = () => {
  const { t } = useTranslation();
  const { aboutValues, aboutStory, companyInfo } = useMockData();
  
  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-20 relative">
        <div className="grid-pattern absolute inset-0 opacity-30" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-semibold text-white mb-6">
              {t('about.pageTitle')}
            </h1>
            <p className="text-xl text-white/70 leading-relaxed">
              {t('about.pageDescription')}
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-4xl md:text-5xl font-semibold text-white mb-8">
                {t('about.storyTitle')}
              </h2>
              <div className="prose prose-invert">
                <p className="text-white/70 text-lg leading-relaxed mb-6">{t('about.story1')}</p>
                <p className="text-white/70 text-lg leading-relaxed mb-6">{t('about.story2')}</p>
                <p className="text-white/70 text-lg leading-relaxed mb-6">{t('about.story3')}</p>
                <p className="text-white/70 text-lg leading-relaxed mb-6">{t('about.story4')}</p>
              </div>
            </div>
            
            {/* Stats */}
            <div className="lg:sticky lg:top-32">
              <div className="grid grid-cols-2 gap-6">
                <div className="p-8 bg-white/[0.02] border border-white/10">
                  <div className="text-5xl font-semibold text-[#00FFD1] mb-2">10+</div>
                  <div className="text-white/70">{t('about.stats.consultants')}</div>
                </div>
                <div className="p-8 bg-white/[0.02] border border-white/10">
                  <div className="text-5xl font-semibold text-[#00FFD1] mb-2">~10</div>
                  <div className="text-white/70">{t('about.stats.projects')}</div>
                </div>
                <div className="p-8 bg-white/[0.02] border border-white/10">
                  <div className="text-5xl font-semibold text-[#00FFD1] mb-2">95%</div>
                  <div className="text-white/70">{t('about.stats.retention')}</div>
                </div>
                <div className="p-8 bg-white/[0.02] border border-white/10">
                  <div className="text-5xl font-semibold text-[#00FFD1] mb-2">10+</div>
                  <div className="text-white/70">{t('about.stats.experience')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-white mb-6">{t('about.valuesTitle')}</h2>
            <p className="text-lg text-white/60">
              {t('about.valuesSubtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutValues.map((value) => {
              const IconComponent = valueIcons[value.title];
              return (
                <div key={value.title} className="p-8 bg-black border border-white/10 hover:border-[#00FFD1]/30 transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#00FFD1]/10 flex items-center justify-center mb-6">
                    {IconComponent && <IconComponent className="w-6 h-6 text-[#00FFD1]" />}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{value.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-semibold text-white mb-6">
                {t('about.howWeWorkTitle')}
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-[#00FFD1] text-black flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                    1
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{t('about.collaboration.title')}</h3>
                    <p className="text-white/60">
                      {t('about.collaboration.description')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-[#00FFD1] text-black flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                    2
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{t('about.coConstruction.title')}</h3>
                    <p className="text-white/60">
                      {t('about.coConstruction.description')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-[#00FFD1] text-black flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                    3
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{t('about.partnership.title')}</h3>
                    <p className="text-white/60">
                      {t('about.partnership.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-10 bg-white/[0.02] border border-white/10">
              <blockquote className="text-2xl text-white font-medium leading-relaxed mb-8">
                "{t('about.quote')}"
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#00FFD1]/20 flex items-center justify-center text-[#00FFD1] font-semibold">
                  SC
                </div>
                <div>
                  <div className="text-white font-medium">{t('about.quoteAuthor')}</div>
                  <div className="text-white/70 text-sm">{t('about.quoteRole')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0A0A0A] border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold text-white mb-6">
            {t('about.wantToWork')}
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto mb-10">
            {t('about.wantToWorkDesc')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-[#00FFD1] text-black hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] px-8 py-4 text-lg font-medium h-auto">
                {t('about.getInTouch')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/case-studies">
              <Button variant="outline" className="bg-transparent text-white border border-white/20 hover:bg-white hover:text-black px-8 py-4 text-lg font-medium h-auto">
                {t('about.seeOurWork')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
